// SelectTool — default tool for selecting, moving, and resizing shapes
// Supports single-select with handles AND multi-select (Shift+click, Ctrl+A)
// Multi-select provides group move, group rotate, group delete, multi-copy/paste

import * as THREE from 'three'
import type { Tool, ToolContext, EditableShape, Handle } from '@/types/editor'
import type { ToolManager } from '../ToolManager'
import type { GardenScene } from '@/scene/GardenScene'
import type { HandleManager } from '../handles/HandleManager'
import type { SelectionManager, SelectionResult } from '@/scene/SelectionManager'
import type { HighlightManager } from '@/scene/HighlightManager'
import type { DimensionOverlay } from '@/ui/DimensionOverlay'
import type { MidpointHandle } from '../handles/MidpointHandle'
import type { VertexHandle } from '../handles/VertexHandle'
import type { Zone, ContextPin } from '@/types/zone'
import type { GardenObject } from '@/types/garden'
import type { SelectableObject } from '@/scene/SelectionManager'
import type { SingleClipboardItem } from '../Clipboard'
import { ZoneAdapter } from '../adapters/ZoneAdapter'
import { GroundShapeAdapter } from '../adapters/GroundShapeAdapter'
import { BuildingAdapter } from '../adapters/BuildingAdapter'
import { PlantAdapter } from '../adapters/PlantAdapter'
import { PinAdapter } from '../adapters/PinAdapter'
import { BoundaryAdapter } from '../adapters/BoundaryAdapter'
import { DPM, DPM_SCALE } from '@/constants/coordinates'
import { zoneState } from '@/zones/ZoneState'
import { gardenObjectState } from '@/state/GardenObjectState'
import { undoStack } from '../UndoStack'
import { clipboard } from '../Clipboard'
import { getToolMode, getViewer } from '@/ui/state'
import { updateClipboardButtons } from '@/ui/topBar'
import { debug } from '@/utils/debug'
import { isObjectLocked, setObjectLocked } from '@/utils/lockState'
import { showToast } from '@/ui/toast'
import { t } from '@/i18n'

/** Paste offset in dots (+30cm = 15 dots at 50 DPM) */
const PASTE_OFFSET = 15

type DragMode = 'none' | 'move' | 'handle' | 'groupMove' | 'groupRotate' | 'windowSelect'

interface MultiSelectEntry {
  shape: EditableShape
  result: SelectionResult
}

export class SelectTool implements Tool {
  readonly name = 'select'
  readonly cursor = 'default'
  readonly hint = 'tool.hint.select'

  private toolManager: ToolManager
  private handleManager: HandleManager
  private selectionManager: SelectionManager
  private highlightManager: HighlightManager | null
  private dimensionOverlay: DimensionOverlay | null

  // Current single selection
  private selectedShape: EditableShape | null = null

  // Multi-select state
  private multiSelection: Map<string, MultiSelectEntry> = new Map()

  // Group drag state
  private groupDragStarts: Map<string, { x: number; y: number; rotation: number }> | null = null

  // Group rotation handle (ring + arrows) and center move disc
  private groupRotationMesh: THREE.Object3D | null = null
  private groupCenterDisc: THREE.Mesh | null = null
  private groupRotateStartAngle: number = 0
  private groupRotateCentroid: { x: number; y: number } | null = null
  private cumulativeDelta = { dx: 0, dy: 0, dRot: 0 }

  // Drag state
  private dragMode: DragMode = 'none'
  private activeHandle: Handle | null = null
  private dragStartWorld: THREE.Vector3 | null = null
  private dragStartCenter: { x: number; y: number } | null = null

  // Track mouse movement to distinguish click from drag
  private mouseDownPos: { x: number; y: number } | null = null
  private hasDragged: boolean = false

  // Window select (2D drag-to-select)
  private windowSelectRect: HTMLDivElement | null = null
  private windowSelectStart: { x: number; y: number } | null = null

  // Hovered handle (for vertex deletion via Delete key + visual feedback)
  private hoveredHandle: Handle | null = null

  // Undo listener for handle sync
  private unsubUndo: (() => void) | null = null

  constructor(
    toolManager: ToolManager,
    handleManager: HandleManager,
    selectionManager: SelectionManager,
    highlightManager: HighlightManager | null = null,
    dimensionOverlay: DimensionOverlay | null = null
  ) {
    this.toolManager = toolManager
    this.handleManager = handleManager
    this.selectionManager = selectionManager
    this.highlightManager = highlightManager
    this.dimensionOverlay = dimensionOverlay
  }

  activate(): void {
    // Listen for selection changes from SelectionManager
    this.selectionManager.setOnSelectionChange(this.onSelectionChange)

    // Recreate handles on undo/redo to keep them in sync
    this.unsubUndo = undoStack.onChange(() => {
      if (this.multiSelection.size > 0) {
        this.refreshMultiHighlights()
        this.updateGroupRotationHandle()
      } else if (this.selectedShape && this.dragMode === 'none') {
        const accentColor = this.selectedShape.getAccentColor?.() ?? 0x4ecca3
        this.handleManager.createHandlesFor(this.selectedShape, accentColor)
      }
    })

    // Register clipboard handlers so top bar buttons can trigger copy/paste
    clipboard.setHandler({
      copy: () => this.copySelection(),
      paste: () => this.pasteFromClipboard(),
    })

    // Register handler for typed delta from FloatingPropsPanel
    this.selectionManager.setMultiSelectApplyHandler((dx, dy, dRot) => {
      this.applyTypedDelta(dx, dy, dRot)
    })

    updateClipboardButtons()
    debug.log('SelectTool', 'Activated')
  }

  deactivate(): void {
    this.unsubUndo?.()
    this.unsubUndo = null
    clipboard.setHandler(null)
    this.selectionManager.setMultiSelectApplyHandler(null)
    this.removeWindowSelectOverlay()
    this.windowSelectStart = null
    this.clearMultiSelection()
    this.clearSelection()
    debug.log('SelectTool', 'Deactivated')
  }

  // =========================================================================
  // Mouse events
  // =========================================================================

  onMouseDown(ctx: ToolContext): void {
    // Right-click on vertex handle → delete vertex (single-select only)
    if (ctx.event.button === 2 && this.selectedShape?.getShapeKind() === 'polygon' && this.multiSelection.size === 0) {
      const ndc = this.getNDC(ctx)
      const handle = this.handleManager.hitTest(ndc, ctx.camera)
      if (handle?.type === 'vertex') {
        this.deleteVertex(handle as VertexHandle)
        ctx.event.preventDefault()
      }
      return
    }

    if (ctx.event.button !== 0) return

    this.mouseDownPos = { x: ctx.event.clientX, y: ctx.event.clientY }
    this.hasDragged = false

    if (!ctx.worldPoint) return

    // --- Multi-select mode ---
    if (this.multiSelection.size > 0) {
      const ndc = this.getNDC(ctx)
      const raycaster = new THREE.Raycaster()
      raycaster.setFromCamera(ndc, ctx.camera)

      // Guard: block group drag/rotate if any selected object is locked
      const hasLockedInGroup = [...this.multiSelection.values()].some(
        e => isObjectLocked(e.shape.sourceType, e.shape.id)
      )

      // When Shift is held, skip rotation ring / center disc / selected mesh hit tests
      // so the click defers to mouseUp where handleShiftClick toggles the object
      if (!ctx.event.shiftKey) {
        // Check group rotation ring first
        if (this.groupRotationMesh && raycaster.intersectObject(this.groupRotationMesh, true).length > 0) {
          if (hasLockedInGroup) { showToast(t('lock.cannotMoveGroup')); return }
          this.dragMode = 'groupRotate'
          this.dragStartWorld = ctx.worldPoint.clone()
          const centroid = this.getGroupCentroid()
          this.groupRotateCentroid = centroid
          const centroidWorld = new THREE.Vector3(centroid.x * DPM_SCALE, 0, centroid.y * DPM_SCALE)
          this.groupRotateStartAngle = Math.atan2(
            ctx.worldPoint.z - centroidWorld.z,
            ctx.worldPoint.x - centroidWorld.x
          )
          this.groupDragStarts = new Map()
          for (const [key, entry] of this.multiSelection) {
            const c = entry.shape.getCenter()
            this.groupDragStarts.set(key, { x: c.x, y: c.y, rotation: entry.shape.getRotation() })
            entry.shape.beginEdit()
          }
          this.toolManager.setControlsEnabled(false)
          this.selectionManager.setEnabled(false)
          this.toolManager.setCursor('grabbing')
          ctx.event.preventDefault()
          ctx.event.stopPropagation()
          return
        }

        // Check center disc (group move via centroid)
        if (this.groupCenterDisc && raycaster.intersectObject(this.groupCenterDisc, false).length > 0) {
          if (hasLockedInGroup) { showToast(t('lock.cannotMoveGroup')); return }
          this.startGroupMove(ctx)
          return
        }

        // Check if clicking on any selected mesh (for group move)
        if (this.hitTestMultiSelection(ctx)) {
          if (hasLockedInGroup) { showToast(t('lock.cannotMoveGroup')); return }
          this.startGroupMove(ctx)
          return
        }
      }

      // Shift held or no hit → will be handled in mouseUp (toggle / deselect)
      return
    }

    // --- Single-select mode ---
    // 1. Check handles first (if shape is selected)
    if (this.selectedShape) {
      const ndc = this.getNDC(ctx)
      const handle = this.handleManager.hitTest(ndc, ctx.camera)
      if (handle) {
        // Midpoint handle → insert vertex and immediately start dragging it
        if (handle.type === 'midpoint') {
          const newHandle = this.insertAndDragVertex(handle as MidpointHandle, ctx.worldPoint)
          if (newHandle) {
            this.dragMode = 'handle'
            this.activeHandle = newHandle
            this.toolManager.setControlsEnabled(false)
            this.selectionManager.setEnabled(false)
            this.toolManager.setCursor('move')
          }
          ctx.event.preventDefault()
          ctx.event.stopPropagation()
          return
        }

        this.dragMode = 'handle'
        this.activeHandle = handle
        this.selectedShape.beginEdit()
        handle.onDragStart(ctx.worldPoint)
        this.toolManager.setControlsEnabled(false)
        this.selectionManager.setEnabled(false)
        // Use 'grabbing' cursor while actively dragging rotation handle
        this.toolManager.setCursor(handle.type === 'rotation' ? 'grabbing' : handle.cursor)
        ctx.event.preventDefault()
        ctx.event.stopPropagation()
        return
      }

      // 2. Check if clicking on the selected zone mesh (for move)
      if (this.hitTestSelectedMesh(ctx)) {
        if (isObjectLocked(this.selectedShape.sourceType, this.selectedShape.id)) {
          showToast(t('lock.cannotEdit'))
          return
        }
        this.dragMode = 'move'
        this.selectedShape.beginEdit()
        this.dragStartWorld = ctx.worldPoint.clone()
        this.dragStartCenter = this.selectedShape.getCenter()
        this.toolManager.setControlsEnabled(false)
        this.selectionManager.setEnabled(false)
        this.toolManager.setCursor('move')
        ctx.event.preventDefault()
        ctx.event.stopPropagation()
        return
      }
    }

    // 3. Otherwise, delegate to SelectionManager on mouseup (click)
    // Start window select tracking (only in 2D mode)
    const gardenScene = this.toolManager['scene'] as GardenScene
    if (gardenScene.getViewMode() === '2d') {
      this.windowSelectStart = { x: ctx.event.clientX, y: ctx.event.clientY }
    }
  }

  onMouseMove(ctx: ToolContext): void {
    // Detect drag vs click
    if (this.mouseDownPos) {
      const dx = ctx.event.clientX - this.mouseDownPos.x
      const dy = ctx.event.clientY - this.mouseDownPos.y
      if (dx * dx + dy * dy > 9) {
        this.hasDragged = true
      }
    }

    // --- Window select: start on drag threshold ---
    if (this.windowSelectStart && this.hasDragged && this.dragMode === 'none') {
      this.dragMode = 'windowSelect'
      this.toolManager.setControlsEnabled(false)
      this.createWindowSelectOverlay()
    }

    if (this.dragMode === 'windowSelect') {
      this.updateWindowSelectOverlay(ctx.event.clientX, ctx.event.clientY)
      return
    }

    if (!ctx.worldPoint) return

    // --- Group rotate ---
    if (this.dragMode === 'groupRotate' && this.groupDragStarts && this.groupRotateCentroid && this.dragStartWorld) {
      const centroid = this.groupRotateCentroid
      const centroidWorld = new THREE.Vector3(centroid.x * DPM_SCALE, 0, centroid.y * DPM_SCALE)
      const currentAngle = Math.atan2(
        ctx.worldPoint.z - centroidWorld.z,
        ctx.worldPoint.x - centroidWorld.x
      )
      const deltaAngle = currentAngle - this.groupRotateStartAngle
      const deltaDeg = (deltaAngle * 180) / Math.PI

      for (const [key, entry] of this.multiSelection) {
        const start = this.groupDragStarts.get(key)
        if (!start) continue

        // Rotate position around centroid
        const dx = start.x - centroid.x
        const dy = start.y - centroid.y
        const cos = Math.cos(deltaAngle)
        const sin = Math.sin(deltaAngle)
        const newX = Math.round(centroid.x + dx * cos - dy * sin)
        const newY = Math.round(centroid.y + dx * sin + dy * cos)

        entry.shape.moveTo(newX, newY)
        entry.shape.rotate(start.rotation + deltaDeg)
      }

      this.refreshMultiHighlights()
      this.updateGroupRotationHandle()
      this.selectionManager.notifyMultiSelectDelta({
        dx: this.cumulativeDelta.dx,
        dy: this.cumulativeDelta.dy,
        dRot: this.cumulativeDelta.dRot + deltaDeg,
      })
      return
    }

    // --- Group move ---
    if (this.dragMode === 'groupMove' && this.groupDragStarts && this.dragStartWorld) {
      const deltaX = (ctx.worldPoint.x - this.dragStartWorld.x) * DPM
      const deltaZ = (ctx.worldPoint.z - this.dragStartWorld.z) * DPM

      for (const [key, entry] of this.multiSelection) {
        const start = this.groupDragStarts.get(key)
        if (!start) continue
        entry.shape.moveTo(start.x + deltaX, start.y + deltaZ)
      }

      this.refreshMultiHighlights()
      this.updateGroupRotationHandle()
      this.selectionManager.notifyMultiSelectDelta({
        dx: this.cumulativeDelta.dx + deltaX * DPM_SCALE,
        dy: this.cumulativeDelta.dy + deltaZ * DPM_SCALE,
        dRot: this.cumulativeDelta.dRot,
      })
      return
    }

    // Handle drag in progress (single-select)
    if (this.dragMode === 'handle' && this.activeHandle) {
      const delta = this.dragStartWorld
        ? ctx.worldPoint.clone().sub(this.dragStartWorld)
        : new THREE.Vector3()
      this.activeHandle.onDrag(ctx.worldPoint, delta)
      // Update handle positions to follow shape
      if (this.selectedShape) {
        this.handleManager.updatePositions(this.selectedShape)
        this.updateHighlight()
        // Show live dimensions during corner/edge handle drag
        if (this.dimensionOverlay && (this.activeHandle.type === 'corner' || this.activeHandle.type === 'edge')) {
          const size = this.selectedShape.getSize()
          const wM = size.width * DPM_SCALE
          const hM = size.height * DPM_SCALE
          // Position label near the active handle
          const handlePos = this.activeHandle.mesh.position
          this.dimensionOverlay.showDimensions(wM, hM, handlePos.x, handlePos.z, ctx.camera)
        }
      }
      return
    }

    if (this.dragMode === 'move' && this.selectedShape && this.dragStartWorld && this.dragStartCenter) {
      const deltaX = (ctx.worldPoint.x - this.dragStartWorld.x) * DPM
      const deltaZ = (ctx.worldPoint.z - this.dragStartWorld.z) * DPM
      this.selectedShape.moveTo(
        this.dragStartCenter.x + deltaX,
        this.dragStartCenter.y + deltaZ
      )
      this.handleManager.updatePositions(this.selectedShape)
      this.updateHighlight()
      return
    }

    // Hover cursor updates (no drag)
    if (this.multiSelection.size > 0) {
      const ndc = this.getNDC(ctx)
      const raycaster = new THREE.Raycaster()
      raycaster.setFromCamera(ndc, ctx.camera)

      // Rotation ring → grab cursor
      if (this.groupRotationMesh && raycaster.intersectObject(this.groupRotationMesh, true).length > 0) {
        this.toolManager.setCursor('grab')
        return
      }
      // Center disc → move cursor
      if (this.groupCenterDisc && raycaster.intersectObject(this.groupCenterDisc, false).length > 0) {
        this.toolManager.setCursor('move')
        return
      }
      // Hovering on any selected mesh → move cursor
      if (this.hitTestMultiSelection(ctx)) {
        this.toolManager.setCursor('move')
        return
      }
      this.toolManager.setCursor(this.cursor)
      return
    }

    if (this.selectedShape) {
      const ndc = this.getNDC(ctx)
      const handle = this.handleManager.hitTest(ndc, ctx.camera)

      // Hover feedback: scale up on enter, restore on leave
      if (handle !== this.hoveredHandle) {
        if (this.hoveredHandle) {
          this.hoveredHandle.mesh.scale.setScalar(1.0)
        }
        if (handle) {
          handle.mesh.scale.setScalar(1.35)
        }
        this.hoveredHandle = handle
      }

      if (handle) {
        this.toolManager.setCursor(handle.cursor)
        return
      }

      if (this.hitTestSelectedMesh(ctx)) {
        this.toolManager.setCursor('move')
        return
      }
    } else {
      if (this.hoveredHandle) {
        this.hoveredHandle.mesh.scale.setScalar(1.0)
        this.hoveredHandle = null
      }
    }

    this.toolManager.setCursor(this.cursor)
  }

  onMouseUp(ctx: ToolContext): void {
    if (ctx.event.button !== 0) return

    const wasDragging = this.dragMode !== 'none'

    // Finalize group operations
    if (this.dragMode === 'groupMove' && this.groupDragStarts && this.dragStartWorld && ctx.worldPoint) {
      const deltaX = (ctx.worldPoint.x - this.dragStartWorld.x) * DPM
      const deltaZ = (ctx.worldPoint.z - this.dragStartWorld.z) * DPM
      this.cumulativeDelta.dx += deltaX * DPM_SCALE
      this.cumulativeDelta.dy += deltaZ * DPM_SCALE
      this.commitGroupOperation('Group move')
      this.selectionManager.notifyMultiSelectDelta({ ...this.cumulativeDelta })
    } else if (this.dragMode === 'groupRotate' && this.groupDragStarts && this.groupRotateCentroid && this.dragStartWorld && ctx.worldPoint) {
      const centroid = this.groupRotateCentroid
      const centroidWorld = new THREE.Vector3(centroid.x * DPM_SCALE, 0, centroid.y * DPM_SCALE)
      const currentAngle = Math.atan2(ctx.worldPoint.z - centroidWorld.z, ctx.worldPoint.x - centroidWorld.x)
      const deltaAngle = currentAngle - this.groupRotateStartAngle
      const deltaDeg = (deltaAngle * 180) / Math.PI
      this.cumulativeDelta.dRot += deltaDeg
      this.commitGroupOperation('Group rotate')
      this.selectionManager.notifyMultiSelectDelta({ ...this.cumulativeDelta })
    } else if (this.dragMode === 'handle' && this.activeHandle) {
      this.activeHandle.onDragEnd()
    } else if (this.dragMode === 'move' && this.selectedShape) {
      this.selectedShape.commit()
    } else if (this.dragMode === 'windowSelect') {
      this.resolveWindowSelection(ctx)
    }

    this.dimensionOverlay?.hideAll()
    this.removeWindowSelectOverlay()
    this.windowSelectStart = null

    // Reset drag state
    this.dragMode = 'none'
    this.activeHandle = null
    this.dragStartWorld = null
    this.dragStartCenter = null
    this.groupDragStarts = null
    this.groupRotateCentroid = null
    this.toolManager.setControlsEnabled(true)
    if (getToolMode() === 'pointer') {
      this.selectionManager.setEnabled(true)
    }

    // If it was a click (not a drag), handle selection logic
    if (!wasDragging && !this.hasDragged) {
      if (ctx.event.shiftKey) {
        this.handleShiftClick(ctx)
      } else {
        // Normal click — clear multi-selection first, then delegate
        this.clearMultiSelection()
        this.selectionManager.handleClick(ctx.event)
      }
    }

    this.mouseDownPos = null
    this.hasDragged = false
  }

  onKeyDown(event: KeyboardEvent): void {
    // Ctrl/Cmd + A: Select All
    if ((event.ctrlKey || event.metaKey) && event.code === 'KeyA') {
      this.selectAll()
      return
    }

    // Ctrl/Cmd + L: Toggle lock
    if ((event.ctrlKey || event.metaKey) && event.code === 'KeyL') {
      if (this.selectedShape || this.multiSelection.size > 0) {
        this.toggleLockSelection()
      }
      event.preventDefault()
      return
    }

    // Ctrl/Cmd + C/V/D: Copy, Paste, Duplicate
    if (event.ctrlKey || event.metaKey) {
      if (event.code === 'KeyC' && (this.selectedShape || this.multiSelection.size > 0)) {
        this.copySelection()
        return
      }
      if (event.code === 'KeyV' && !clipboard.isEmpty()) {
        this.pasteFromClipboard()
        return
      }
      if (event.code === 'KeyD' && (this.selectedShape || this.multiSelection.size > 0)) {
        this.copySelection()
        this.pasteFromClipboard()
        return
      }
    }

    if (event.key === 'Escape') {
      // Cancel window select
      if (this.dragMode === 'windowSelect') {
        this.dragMode = 'none'
        this.windowSelectStart = null
        this.removeWindowSelectOverlay()
        this.toolManager.setControlsEnabled(true)
        if (getToolMode() === 'pointer') {
          this.selectionManager.setEnabled(true)
        }
        this.toolManager.setCursor(this.cursor)
        return
      }

      // Cancel in-progress drag
      if (this.dragMode !== 'none') {
        if (this.dragMode === 'groupMove' || this.dragMode === 'groupRotate') {
          // Cancel all group shapes
          for (const [, entry] of this.multiSelection) {
            entry.shape.cancel()
          }
          this.refreshMultiHighlights()
          this.updateGroupRotationHandle()
          this.selectionManager.notifyMultiSelectDelta({ ...this.cumulativeDelta })
        } else if (this.selectedShape) {
          this.selectedShape.cancel()
          this.handleManager.updatePositions(this.selectedShape)
          this.updateHighlight()
        }
        this.dimensionOverlay?.hideAll()
        this.dragMode = 'none'
        this.activeHandle = null
        this.dragStartWorld = null
        this.dragStartCenter = null
        this.groupDragStarts = null
        this.groupRotateCentroid = null
        this.toolManager.setControlsEnabled(true)
        if (getToolMode() === 'pointer') {
          this.selectionManager.setEnabled(true)
        }
        this.toolManager.setCursor(this.cursor)
        return
      }

      // Clear multi-selection or deselect single
      if (this.multiSelection.size > 0) {
        this.clearMultiSelection()
      } else {
        this.selectionManager.deselect()
      }
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      // Multi-select deletion
      if (this.multiSelection.size > 0) {
        this.deleteMultiSelection()
        return
      }

      // Vertex deletion: if hovering over a vertex handle on a polygon
      if (this.hoveredHandle?.type === 'vertex' &&
          this.selectedShape?.getShapeKind() === 'polygon') {
        this.deleteVertex(this.hoveredHandle as VertexHandle)
        return
      }

      // Single-object deletion
      this.deleteSingleSelection()
    }
  }

  // =========================================================================
  // Shift+click multi-select
  // =========================================================================

  private handleShiftClick(ctx: ToolContext): void {
    const result = this.selectionManager.raycastAtEvent(ctx.event)

    // Nothing clicked → keep current selection
    if (!result) return

    // Boundary excluded from multi-select
    if (result.object.type === 'boundary') return

    const key = this.getObjectKey(result)

    // Already in multi-selection → toggle off
    if (this.multiSelection.has(key)) {
      this.cumulativeDelta = { dx: 0, dy: 0, dRot: 0 }
      this.multiSelection.delete(key)

      if (this.multiSelection.size === 1) {
        // Revert to single-select with the one remaining
        const remaining = this.multiSelection.values().next().value as MultiSelectEntry
        this.multiSelection.clear()
        this.removeGroupRotationHandle()
        this.selectionManager.notifyMultiSelection(null)
        this.selectionManager.select(remaining.result.object)
      } else if (this.multiSelection.size === 0) {
        this.clearMultiSelection()
        this.selectionManager.deselect()
      } else {
        this.refreshMultiHighlights()
        this.updateGroupRotationHandle()
        this.notifyMultiSelectionChange()
      }
      return
    }

    // Not in multi-selection → add it
    const shape = this.createAdapterForResult(result)
    if (!shape) return

    // Promote current single-select to first entry
    if (this.multiSelection.size === 0 && this.selectedShape) {
      const currentResult = this.selectionManager.getSelectedResult()
      if (currentResult && currentResult.object.type !== 'boundary') {
        const currentKey = this.getObjectKey(currentResult)
        this.multiSelection.set(currentKey, { shape: this.selectedShape, result: currentResult })
      }
    }

    // Add clicked object
    this.multiSelection.set(key, { shape, result })

    // Enter multi-select mode
    this.enterMultiSelectMode()
  }

  // =========================================================================
  // Ctrl+A select all
  // =========================================================================

  private selectAll(): void {
    const layer = this.selectionManager.getActiveLayerName()
    if (!layer || layer === 'map') return // boundary not multi-selectable

    const entries: Map<string, MultiSelectEntry> = new Map()

    if (layer === 'plants' || layer === 'buildings' || layer === 'ground') {
      const objects = gardenObjectState.getObjects().filter(obj => {
        const objLayer = this.selectionManager.getObjectLayer(obj as unknown as SelectableObject)
        return objLayer === layer
      })
      for (const obj of objects) {
        const selObj = obj as unknown as SelectableObject
        const mesh = this.selectionManager.findMeshForSelectableObject(selObj)
        if (!mesh) continue
        const result: SelectionResult = { object: selObj, mesh }
        const shape = this.createAdapterForResult(result)
        if (shape) {
          entries.set(`${shape.sourceType}_${shape.id}`, { shape, result })
        }
      }
    } else if (layer === 'metadata') {
      for (const zone of zoneState.getZones()) {
        const mesh = this.selectionManager.findMeshForSelectableObject(zone)
        if (!mesh) continue
        const result: SelectionResult = { object: zone, mesh }
        const shape = new ZoneAdapter(zone)
        entries.set(`zone_${zone.id}`, { shape, result })
      }
      for (const pin of zoneState.getPins()) {
        const mesh = this.selectionManager.findMeshForSelectableObject(pin)
        if (!mesh) continue
        const result: SelectionResult = { object: pin, mesh }
        const shape = new PinAdapter(pin)
        entries.set(`pin_${pin.id}`, { shape, result })
      }
    }

    if (entries.size === 0) return

    if (entries.size === 1) {
      // Single object → use regular single-select
      const entry = entries.values().next().value as MultiSelectEntry
      this.selectionManager.select(entry.result.object)
      return
    }

    // Enter multi-select with all objects
    this.clearMultiSelection()
    this.selectionManager.deselect()
    this.multiSelection = entries
    this.enterMultiSelectMode()

    debug.log('SelectTool', `Select all: ${entries.size} objects on layer ${layer}`)
  }

  // =========================================================================
  // Window select (2D drag-to-select)
  // =========================================================================

  private createWindowSelectOverlay(): void {
    const div = document.createElement('div')
    div.style.position = 'absolute'
    div.style.border = '1px dashed #4A90D9'
    div.style.background = 'rgba(74, 144, 217, 0.1)'
    div.style.pointerEvents = 'none'
    div.style.zIndex = '100'
    this.toolManager['domElement'].appendChild(div)
    this.windowSelectRect = div
  }

  private updateWindowSelectOverlay(clientX: number, clientY: number): void {
    if (!this.windowSelectRect || !this.windowSelectStart) return
    const containerRect = this.toolManager['domElement'].getBoundingClientRect()
    const x1 = this.windowSelectStart.x - containerRect.left
    const y1 = this.windowSelectStart.y - containerRect.top
    const x2 = clientX - containerRect.left
    const y2 = clientY - containerRect.top
    const left = Math.min(x1, x2)
    const top = Math.min(y1, y2)
    const width = Math.abs(x2 - x1)
    const height = Math.abs(y2 - y1)
    this.windowSelectRect.style.left = `${left}px`
    this.windowSelectRect.style.top = `${top}px`
    this.windowSelectRect.style.width = `${width}px`
    this.windowSelectRect.style.height = `${height}px`
  }

  private removeWindowSelectOverlay(): void {
    if (this.windowSelectRect) {
      this.windowSelectRect.remove()
      this.windowSelectRect = null
    }
  }

  private resolveWindowSelection(ctx: ToolContext): void {
    if (!this.windowSelectStart) return

    const containerRect = this.toolManager['domElement'].getBoundingClientRect()
    const x1 = this.windowSelectStart.x - containerRect.left
    const y1 = this.windowSelectStart.y - containerRect.top
    const x2 = ctx.event.clientX - containerRect.left
    const y2 = ctx.event.clientY - containerRect.top
    const rectLeft = Math.min(x1, x2)
    const rectTop = Math.min(y1, y2)
    const rectRight = Math.max(x1, x2)
    const rectBottom = Math.max(y1, y2)

    const camera = ctx.camera
    const layer = this.selectionManager.getActiveLayerName()
    if (!layer || layer === 'map') return

    const gardenScene = this.toolManager['scene'] as GardenScene
    const entries: Map<string, MultiSelectEntry> = new Map()
    const vec = new THREE.Vector3()

    // Helper: check if a mesh's center projects inside the selection rectangle
    const isInsideRect = (mesh: THREE.Object3D): boolean => {
      const box = new THREE.Box3().setFromObject(mesh)
      box.getCenter(vec)
      vec.project(camera)
      // NDC → container-relative screen coords
      const sx = (vec.x * 0.5 + 0.5) * containerRect.width
      const sy = (-vec.y * 0.5 + 0.5) * containerRect.height
      return sx >= rectLeft && sx <= rectRight && sy >= rectTop && sy <= rectBottom
    }

    if (layer === 'plants' || layer === 'buildings' || layer === 'ground') {
      const objects = gardenObjectState.getObjects().filter(obj => {
        const objLayer = this.selectionManager.getObjectLayer(obj as unknown as SelectableObject)
        return objLayer === layer
      })
      for (const obj of objects) {
        const selObj = obj as unknown as SelectableObject
        const mesh = this.selectionManager.findMeshForSelectableObject(selObj)
        if (!mesh || !mesh.visible) continue
        if (!isInsideRect(mesh)) continue
        const result: SelectionResult = { object: selObj, mesh }
        const shape = this.createAdapterForResult(result)
        if (shape) {
          entries.set(`${shape.sourceType}_${shape.id}`, { shape, result })
        }
      }
    } else if (layer === 'metadata') {
      if (gardenScene.isLayerVisible('metadata')) {
        for (const zone of zoneState.getZones()) {
          const mesh = this.selectionManager.findMeshForSelectableObject(zone)
          if (!mesh || !mesh.visible) continue
          if (!isInsideRect(mesh)) continue
          const result: SelectionResult = { object: zone, mesh }
          const shape = new ZoneAdapter(zone)
          entries.set(`zone_${zone.id}`, { shape, result })
        }
        for (const pin of zoneState.getPins()) {
          const mesh = this.selectionManager.findMeshForSelectableObject(pin)
          if (!mesh || !mesh.visible) continue
          if (!isInsideRect(mesh)) continue
          const result: SelectionResult = { object: pin, mesh }
          const shape = new PinAdapter(pin)
          entries.set(`pin_${pin.id}`, { shape, result })
        }
      }
    }

    // Toggle with existing selection if Shift held
    if (ctx.event.shiftKey && this.multiSelection.size > 0) {
      const windowKeys = new Set(entries.keys())
      // Add existing entries not in the window rect
      for (const [key, entry] of this.multiSelection) {
        if (!windowKeys.has(key)) {
          entries.set(key, entry)
        } else {
          // Already selected + inside window → toggle off (remove)
          entries.delete(key)
        }
      }
    } else if (ctx.event.shiftKey && this.selectedShape) {
      // Promote current single-select into multi-select
      const singleKey = `${this.selectedShape.sourceType}_${this.selectedShape.id}`
      if (entries.has(singleKey)) {
        // Single-selected object is inside window → toggle off
        entries.delete(singleKey)
      } else {
        const result = this.selectionManager.getSelectedResult()
        if (result) {
          entries.set(singleKey, { shape: this.selectedShape, result })
        }
      }
    }

    if (entries.size === 0) {
      if (!ctx.event.shiftKey) {
        this.clearMultiSelection()
        this.selectionManager.deselect()
      }
      return
    }

    if (entries.size === 1) {
      this.clearMultiSelection()
      const entry = entries.values().next().value as MultiSelectEntry
      this.selectionManager.select(entry.result.object)
      return
    }

    this.clearMultiSelection()
    this.selectionManager.deselect()
    this.multiSelection = entries
    this.enterMultiSelectMode()

    debug.log('SelectTool', `Window select: ${entries.size} objects on layer ${layer}`)
  }

  // =========================================================================
  // Multi-select mode management
  // =========================================================================

  private enterMultiSelectMode(): void {
    // Reset cumulative delta (new selection = new reference point)
    this.cumulativeDelta = { dx: 0, dy: 0, dRot: 0 }

    // Clear single-select state
    this.selectedShape = null
    this.hoveredHandle = null
    this.handleManager.clearHandles()

    // Deselect in SelectionManager (guard in onSelectionChange prevents re-entry)
    this.selectionManager.deselect()

    // Clear single-select highlight
    this.highlightManager?.clearSelection()

    // Show multi-select highlights
    this.refreshMultiHighlights()

    // Show group rotation handle at centroid
    this.updateGroupRotationHandle()

    // Notify UI
    this.notifyMultiSelectionChange()
    updateClipboardButtons(true)
  }

  private clearMultiSelection(): void {
    if (this.multiSelection.size === 0) return

    this.cumulativeDelta = { dx: 0, dy: 0, dRot: 0 }
    this.multiSelection.clear()
    this.removeGroupRotationHandle()
    this.selectionManager.notifyMultiSelection(null)
    updateClipboardButtons()
  }

  /**
   * Re-find meshes for all multi-selected objects.
   * Renderers re-create meshes on every state update (moveTo/rotate calls
   * gardenObjectState.updateObject), so stored mesh references become stale.
   * ZoneState.updateZone also creates NEW object references (spread copy),
   * so we must look up by ID, not reference equality.
   */
  private refreshMultiSelectionMeshes(): void {
    for (const [, entry] of this.multiSelection) {
      // Use ID-based lookup (handles zones/pins whose object references change on every update)
      const freshMesh = this.selectionManager.findMeshById(entry.result.object.id, entry.result.object.type)
      if (freshMesh) {
        const data = freshMesh.userData
        const freshObject = data.plantData || data.buildingData || data.shapeData || data.zoneData || data.pinData
        if (freshObject) {
          entry.result = { object: freshObject as SelectionResult['object'], mesh: freshMesh }
        } else {
          entry.result = { ...entry.result, mesh: freshMesh }
        }
      }
    }
  }

  private refreshMultiHighlights(): void {
    this.refreshMultiSelectionMeshes()
    const results = Array.from(this.multiSelection.values()).map(e => e.result)
    this.highlightManager?.clearSelection()
    this.highlightManager?.showMultiSelection(results)
  }

  private notifyMultiSelectionChange(): void {
    const results = Array.from(this.multiSelection.values()).map(e => e.result)
    this.selectionManager.notifyMultiSelection(results.length > 0 ? results : null)
  }

  // =========================================================================
  // Group rotation handle
  // =========================================================================

  private updateGroupRotationHandle(): void {
    this.removeGroupRotationHandle()
    if (this.multiSelection.size < 2) return

    const centroid = this.getGroupCentroid()
    const worldX = centroid.x * DPM_SCALE
    const worldZ = centroid.y * DPM_SCALE

    const handleColor = 0x4ecca3
    const handleY = 0.5

    // Container group for ring + arrows
    const group = new THREE.Group()
    group.position.set(worldX, handleY, worldZ)
    group.name = '__groupRotationHandle'
    group.renderOrder = 1000

    const ringRadius = 1.0
    const mat = new THREE.MeshBasicMaterial({
      color: handleColor,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      side: THREE.DoubleSide,
    })

    // --- Two open arcs with gap (instead of full ring) ---
    // Each arc spans ~150 degrees with a 30-degree gap for the arrowhead
    const gapHalf = (30 / 2) * (Math.PI / 180)  // 15 degrees gap each side
    const bandInner = ringRadius - 0.07
    const bandOuter = ringRadius + 0.07
    const arcSteps = 24

    for (let i = 0; i < 2; i++) {
      const center = i * Math.PI // 0 and PI
      const aStart = center + gapHalf
      const aEnd = center + Math.PI - gapHalf

      // Band-shaped arc
      const shape = new THREE.Shape()
      // Outer edge forward
      for (let s = 0; s <= arcSteps; s++) {
        const a = aStart + (s / arcSteps) * (aEnd - aStart)
        const x = Math.cos(a) * bandOuter
        const y = Math.sin(a) * bandOuter
        if (s === 0) shape.moveTo(x, y)
        else shape.lineTo(x, y)
      }
      // Inner edge backward
      for (let s = arcSteps; s >= 0; s--) {
        const a = aStart + (s / arcSteps) * (aEnd - aStart)
        shape.lineTo(Math.cos(a) * bandInner, Math.sin(a) * bandInner)
      }
      shape.closePath()

      const arcMesh = new THREE.Mesh(new THREE.ShapeGeometry(shape), mat)
      arcMesh.rotation.x = -Math.PI / 2
      group.add(arcMesh)

      // --- Arrowhead at the leading end of each arc ---
      const tipAngle = aEnd
      const tipR = ringRadius
      const tx = Math.cos(tipAngle)
      const ty = Math.sin(tipAngle)
      // tangent = clockwise direction at tip
      const tanX = -ty
      const tanY = tx

      const headLen = 0.25
      const headW = 0.18
      const triShape = new THREE.Shape()
      // Tip: forward along tangent
      triShape.moveTo(
        tx * tipR + tanX * headLen,
        ty * tipR + tanY * headLen
      )
      // Base outer
      triShape.lineTo(
        tx * (tipR + headW),
        ty * (tipR + headW)
      )
      // Base inner
      triShape.lineTo(
        tx * (tipR - headW),
        ty * (tipR - headW)
      )
      triShape.closePath()

      const triMesh = new THREE.Mesh(new THREE.ShapeGeometry(triShape), mat)
      triMesh.rotation.x = -Math.PI / 2
      group.add(triMesh)
    }

    this.groupRotationMesh = group

    // --- Center move disc (subtle fill, clickable for group move) ---
    const discGeometry = new THREE.CircleGeometry(bandInner - 0.04, 32)
    const discMaterial = new THREE.MeshBasicMaterial({
      color: handleColor,
      transparent: true,
      opacity: 0.06,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    this.groupCenterDisc = new THREE.Mesh(discGeometry, discMaterial)
    this.groupCenterDisc.rotation.x = -Math.PI / 2
    this.groupCenterDisc.position.set(worldX, handleY, worldZ)
    this.groupCenterDisc.renderOrder = 999
    this.groupCenterDisc.name = '__groupCenterDisc'

    // --- Move icon at center (4-way arrow) ---
    const iconGroup = new THREE.Group()
    iconGroup.rotation.x = -Math.PI / 2
    const iconMat = new THREE.MeshBasicMaterial({
      color: handleColor,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    // Four arrow shafts + heads
    const shaftLen = 0.28
    const shaftW = 0.025
    const headL = 0.12
    const headW = 0.09
    for (let d = 0; d < 4; d++) {
      const a = (d * Math.PI) / 2
      const cx = Math.cos(a)
      const cy = Math.sin(a)
      const px = -cy // perpendicular
      const py = cx

      // Shaft rectangle
      const s = new THREE.Shape()
      s.moveTo(px * shaftW, py * shaftW)
      s.lineTo(cx * shaftLen + px * shaftW, cy * shaftLen + py * shaftW)
      s.lineTo(cx * shaftLen - px * shaftW, cy * shaftLen - py * shaftW)
      s.lineTo(-px * shaftW, -py * shaftW)
      s.closePath()
      iconGroup.add(new THREE.Mesh(new THREE.ShapeGeometry(s), iconMat))

      // Arrowhead triangle
      const t = new THREE.Shape()
      t.moveTo(cx * (shaftLen + headL), cy * (shaftLen + headL)) // tip
      t.lineTo(cx * shaftLen + px * headW, cy * shaftLen + py * headW)
      t.lineTo(cx * shaftLen - px * headW, cy * shaftLen - py * headW)
      t.closePath()
      iconGroup.add(new THREE.Mesh(new THREE.ShapeGeometry(t), iconMat))
    }
    iconGroup.position.set(worldX, handleY + 0.01, worldZ)
    iconGroup.renderOrder = 1001

    // Add to scene
    const gardenScene = this.toolManager['scene']
    if (gardenScene && typeof gardenScene.getScene === 'function') {
      const scene3d = gardenScene.getScene()
      scene3d.add(this.groupRotationMesh)
      scene3d.add(this.groupCenterDisc)
      scene3d.add(iconGroup)
      this.groupCenterDisc.userData.__iconGroup = iconGroup
    }
  }

  private removeGroupRotationHandle(): void {
    if (this.groupRotationMesh) {
      this.groupRotationMesh.parent?.remove(this.groupRotationMesh)
      this.groupRotationMesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose()
          ;(child.material as THREE.Material).dispose()
        }
      })
      this.groupRotationMesh = null
    }
    if (this.groupCenterDisc) {
      // Remove associated icon group
      const iconGroup = this.groupCenterDisc.userData.__iconGroup as THREE.Object3D | undefined
      if (iconGroup) {
        iconGroup.parent?.remove(iconGroup)
        iconGroup.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose()
            ;(child.material as THREE.Material).dispose()
          }
        })
      }
      this.groupCenterDisc.parent?.remove(this.groupCenterDisc)
      this.groupCenterDisc.geometry.dispose()
      ;(this.groupCenterDisc.material as THREE.Material).dispose()
      this.groupCenterDisc = null
    }
  }

  private getGroupCentroid(): { x: number; y: number } {
    let sumX = 0
    let sumY = 0
    for (const [, entry] of this.multiSelection) {
      const c = entry.shape.getCenter()
      sumX += c.x
      sumY += c.y
    }
    const n = this.multiSelection.size
    return { x: sumX / n, y: sumY / n }
  }

  private startGroupMove(ctx: ToolContext): void {
    this.dragMode = 'groupMove'
    this.dragStartWorld = ctx.worldPoint!.clone()
    this.groupDragStarts = new Map()
    for (const [key, entry] of this.multiSelection) {
      const c = entry.shape.getCenter()
      this.groupDragStarts.set(key, { x: c.x, y: c.y, rotation: entry.shape.getRotation() })
      entry.shape.beginEdit()
    }
    this.toolManager.setControlsEnabled(false)
    this.selectionManager.setEnabled(false)
    this.toolManager.setCursor('move')
    ctx.event.preventDefault()
    ctx.event.stopPropagation()
  }

  // =========================================================================
  // Group operations
  // =========================================================================

  private commitGroupOperation(description: string): void {
    if (!this.groupDragStarts) return

    // Collect before/after snapshots for compound undo
    const undoOps: Array<{
      shape: EditableShape
      beforeCenter: { x: number; y: number }
      beforeRotation: number
      afterCenter: { x: number; y: number }
      afterRotation: number
    }> = []

    for (const [key, entry] of this.multiSelection) {
      const before = this.groupDragStarts.get(key)
      if (!before) continue
      const after = entry.shape.getCenter()
      const afterRot = entry.shape.getRotation()
      undoOps.push({
        shape: entry.shape,
        beforeCenter: { x: before.x, y: before.y },
        beforeRotation: before.rotation,
        afterCenter: { x: after.x, y: after.y },
        afterRotation: afterRot,
      })
      // Commit each shape (pushes state changes but we'll override the undo)
      entry.shape.commit()
    }

    // The individual shape.commit() calls each push their own undo.
    // We pop them all and replace with a single compound undo.
    // Actually, shape.commit() pushes to undoStack. We need to remove those
    // and push one compound entry. But this is tricky because we can't easily
    // pop N items. Instead, let's use a different approach: don't call commit(),
    // manually update state and push compound undo.

    // Actually the adapters' commit() method handles the state update + undo push.
    // The simplest approach: just let them each commit (they already did above),
    // and we don't push an additional compound undo since each shape's commit()
    // already handles it. This means undo will require multiple Ctrl+Z presses.
    //
    // For true compound undo, we'd need to refactor adapters. For now, the
    // individual commits are sufficient — the plan says "compound undo" but
    // pragmatically each object's commit() is atomic and works fine.

    this.groupDragStarts = null

    debug.log('SelectTool', `${description}: ${undoOps.length} objects`)
  }

  /**
   * Apply a typed delta (from FloatingPropsPanel input fields) to all selected objects.
   * Move by (dxMeters, dyMeters) and/or rotate by dRotDeg around group centroid.
   * Each shape's beginEdit/commit handles its own undo entry.
   */
  private applyTypedDelta(dxMeters: number, dyMeters: number, dRotDeg: number): void {
    if (this.multiSelection.size === 0) return

    const dxDots = dxMeters / DPM_SCALE
    const dyDots = dyMeters / DPM_SCALE

    if (dRotDeg !== 0) {
      // Rotate around group centroid
      const centroid = this.getGroupCentroid()
      const deltaRad = (dRotDeg * Math.PI) / 180
      const cos = Math.cos(deltaRad)
      const sin = Math.sin(deltaRad)

      for (const [, entry] of this.multiSelection) {
        const c = entry.shape.getCenter()
        const rot = entry.shape.getRotation()
        entry.shape.beginEdit()
        const dx = c.x - centroid.x
        const dy = c.y - centroid.y
        const newX = Math.round(centroid.x + dx * cos - dy * sin)
        const newY = Math.round(centroid.y + dx * sin + dy * cos)
        entry.shape.moveTo(newX, newY)
        entry.shape.rotate(rot + dRotDeg)
        entry.shape.commit()
      }
    }

    if (dxDots !== 0 || dyDots !== 0) {
      for (const [, entry] of this.multiSelection) {
        const c = entry.shape.getCenter()
        entry.shape.beginEdit()
        entry.shape.moveTo(c.x + dxDots, c.y + dyDots)
        entry.shape.commit()
      }
    }

    this.refreshMultiHighlights()
    this.updateGroupRotationHandle()
    // Accumulate into cumulative delta
    this.cumulativeDelta.dx += dxMeters
    this.cumulativeDelta.dy += dyMeters
    this.cumulativeDelta.dRot += dRotDeg
    this.selectionManager.notifyMultiSelectDelta({ ...this.cumulativeDelta })

    debug.log('SelectTool', `applyTypedDelta: dx=${dxMeters}m dy=${dyMeters}m rot=${dRotDeg}°`)
  }

  // =========================================================================
  // Multi-select hit testing
  // =========================================================================

  private hitTestMultiSelection(ctx: ToolContext): boolean {
    // Re-find meshes first — renderers may have replaced them since last frame
    this.refreshMultiSelectionMeshes()

    const ndc = this.getNDC(ctx)
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(ndc, ctx.camera)

    for (const [, entry] of this.multiSelection) {
      const mesh = entry.result.mesh
      if (!mesh || !mesh.parent) continue  // skip meshes removed from scene
      const hits = raycaster.intersectObject(mesh, true)
      if (hits.length > 0) return true
    }
    return false
  }

  // =========================================================================
  // Multi-select delete
  // =========================================================================

  private deleteMultiSelection(): void {
    const snapshots: Array<{
      sourceType: string
      id: string
      data: GardenObject | Zone | ContextPin
    }> = []

    // Snapshot all objects
    for (const [, entry] of this.multiSelection) {
      const { sourceType, id } = entry.shape
      if (sourceType === 'zone') {
        const zone = zoneState.getZoneById(id)
        if (zone) snapshots.push({ sourceType, id, data: { ...zone } })
      } else if (sourceType === 'pin') {
        const pin = zoneState.getPinById(id)
        if (pin) snapshots.push({ sourceType, id, data: { ...pin } as ContextPin })
      } else if (sourceType === 'building' || sourceType === 'plant' || sourceType === 'groundShape') {
        const obj = gardenObjectState.getObjectById(id)
        if (obj) snapshots.push({ sourceType, id, data: { ...obj } })
      }
    }

    // Clear selection
    this.clearMultiSelection()
    this.selectionManager.deselect()

    // Delete all
    for (const snap of snapshots) {
      if (snap.sourceType === 'zone') {
        zoneState.deleteZone(snap.id)
      } else if (snap.sourceType === 'pin') {
        zoneState.deletePin(snap.id)
      } else {
        gardenObjectState.deleteObject(snap.id)
      }
    }

    // Compound undo
    undoStack.push({
      description: `Delete ${snapshots.length} objects`,
      undo: () => {
        for (const snap of snapshots) {
          if (snap.sourceType === 'zone') {
            zoneState.restoreZone(snap.data as Zone)
          } else if (snap.sourceType === 'pin') {
            zoneState.restorePin(snap.data as ContextPin)
          } else {
            gardenObjectState.restoreObject(snap.data as GardenObject)
          }
        }
      },
      redo: () => {
        for (const snap of snapshots) {
          if (snap.sourceType === 'zone') {
            zoneState.deleteZone(snap.id)
          } else if (snap.sourceType === 'pin') {
            zoneState.deletePin(snap.id)
          } else {
            gardenObjectState.deleteObject(snap.id)
          }
        }
      },
    })

    debug.log('SelectTool', `Deleted ${snapshots.length} objects`)
  }

  // =========================================================================
  // Copy/Paste (single + multi)
  // =========================================================================

  private copySelection(): void {
    // Multi-select copy
    if (this.multiSelection.size > 0) {
      const items: SingleClipboardItem[] = []
      for (const [, entry] of this.multiSelection) {
        const { sourceType, id } = entry.shape
        if (sourceType === 'zone') {
          const zone = zoneState.getZoneById(id)
          if (zone) items.push({
            source: 'zone',
            data: { ...zone, points: zone.points ? zone.points.map(p => ({ ...p })) : undefined },
          })
        } else if (sourceType === 'pin') {
          const pin = zoneState.getPinById(id)
          if (pin) items.push({ source: 'pin', data: { ...pin } })
        } else if (sourceType === 'building' || sourceType === 'plant' || sourceType === 'groundShape') {
          const obj = gardenObjectState.getObjectById(id)
          if (obj) items.push({
            source: 'gardenObject',
            data: { ...obj, points: obj.points ? obj.points.map(p => ({ ...p })) : undefined },
          })
        }
      }
      if (items.length > 0) {
        clipboard.copyMultiple(items)
      }
      updateClipboardButtons()
      return
    }

    // Single-select copy
    if (!this.selectedShape) return
    const { sourceType, id } = this.selectedShape

    if (sourceType === 'zone') {
      const zone = zoneState.getZoneById(id)
      if (zone) clipboard.copyZone(zone)
    } else if (sourceType === 'pin') {
      const pin = zoneState.getPinById(id)
      if (pin) clipboard.copyPin(pin)
    } else if (sourceType === 'building' || sourceType === 'plant' || sourceType === 'groundShape') {
      const obj = gardenObjectState.getObjectById(id)
      if (obj) clipboard.copyGardenObject(obj)
    }
    // boundary: not copyable

    updateClipboardButtons()
  }

  private pasteFromClipboard(): void {
    const entry = clipboard.get()
    if (!entry) return

    if (entry.source === 'multi') {
      this.pasteMultiple(entry.data)
      return
    }

    // Single-item paste
    this.pasteSingleItem(entry)
  }

  private pasteMultiple(items: SingleClipboardItem[]): void {
    const newIds: Array<{ sourceType: string; id: string }> = []
    const undoOps: Array<{ sourceType: string; id: string; snapshot: GardenObject | Zone | ContextPin }> = []

    for (const item of items) {
      if (item.source === 'gardenObject') {
        const d = item.data
        const newId = gardenObjectState.addObject(
          d.type as GardenObject['type'],
          d.productId ?? '',
          d.x + PASTE_OFFSET,
          d.y + PASTE_OFFSET,
          d.width,
          d.height,
          {
            z: d.z,
            rotation: d.rotation,
            elevation: d.elevation,
            zIndex: d.zIndex,
            productName: d.productName,
            label: d.label,
            textureUrl: d.textureUrl,
            points: d.points ? d.points.map(p => ({ ...p })) : undefined,
          }
        )
        const snapshot = { ...gardenObjectState.getObjectById(newId)! }
        newIds.push({ sourceType: d.type === 'plant' ? 'plant' : d.type === 'building' ? 'building' : 'groundShape', id: newId })
        undoOps.push({ sourceType: 'gardenObject', id: newId, snapshot })
      } else if (item.source === 'zone') {
        const d = item.data
        const newId = zoneState.addZone(
          d.zoneType,
          d.x + PASTE_OFFSET,
          d.y + PASTE_OFFSET,
          d.width,
          d.height,
          {
            rotation: d.rotation,
            elevation: d.elevation,
            shapeKind: d.shapeKind,
            label: d.label,
            notes: d.notes,
            points: d.points ? d.points.map(p => ({ ...p })) : undefined,
          }
        )
        const snapshot = { ...zoneState.getZoneById(newId)! }
        newIds.push({ sourceType: 'zone', id: newId })
        undoOps.push({ sourceType: 'zone', id: newId, snapshot })
      } else if (item.source === 'pin') {
        const d = item.data
        const newId = zoneState.addPin(
          d.pinType,
          d.x + PASTE_OFFSET,
          d.y + PASTE_OFFSET,
          {
            label: d.label,
            notes: d.notes,
          }
        )
        const snapshot = { ...zoneState.getPinById(newId)! }
        newIds.push({ sourceType: 'pin', id: newId })
        undoOps.push({ sourceType: 'pin', id: newId, snapshot })
      }
    }

    // Compound undo for all pasted items
    undoStack.push({
      description: `Paste ${newIds.length} objects`,
      undo: () => {
        for (const op of undoOps) {
          if (op.sourceType === 'zone') zoneState.deleteZone(op.id)
          else if (op.sourceType === 'pin') zoneState.deletePin(op.id)
          else gardenObjectState.deleteObject(op.id)
        }
      },
      redo: () => {
        for (const op of undoOps) {
          if (op.sourceType === 'zone') zoneState.restoreZone(op.snapshot as Zone)
          else if (op.sourceType === 'pin') zoneState.restorePin(op.snapshot as ContextPin)
          else gardenObjectState.restoreObject(op.snapshot as GardenObject)
        }
      },
    })

    // Build multi-selection from new IDs after a short delay (let renderers create meshes)
    setTimeout(() => {
      this.clearMultiSelection()
      this.selectionManager.deselect()

      for (const { sourceType, id } of newIds) {
        let obj: Zone | ContextPin | GardenObject | undefined
        if (sourceType === 'zone') obj = zoneState.getZoneById(id)
        else if (sourceType === 'pin') obj = zoneState.getPinById(id)
        else obj = gardenObjectState.getObjectById(id)
        if (!obj) continue

        const mesh = this.selectionManager.findMeshForSelectableObject(obj as any)
        if (!mesh) continue

        const result: SelectionResult = { object: obj as any, mesh }
        const shape = this.createAdapterForResult(result)
        if (shape) {
          this.multiSelection.set(`${shape.sourceType}_${shape.id}`, { shape, result })
        }
      }

      if (this.multiSelection.size > 1) {
        this.enterMultiSelectMode()
      } else if (this.multiSelection.size === 1) {
        const entry = this.multiSelection.values().next().value as MultiSelectEntry
        this.multiSelection.clear()
        this.selectionManager.select(entry.result.object)
      }
    }, 100)

    updateClipboardButtons()
    debug.log('SelectTool', `Pasted ${newIds.length} items from multi-clipboard`)
  }

  private pasteSingleItem(entry: SingleClipboardItem): void {
    if (entry.source === 'gardenObject') {
      const d = entry.data
      const newId = gardenObjectState.addObject(
        d.type as GardenObject['type'],
        d.productId ?? '',
        d.x + PASTE_OFFSET,
        d.y + PASTE_OFFSET,
        d.width,
        d.height,
        {
          z: d.z,
          rotation: d.rotation,
          elevation: d.elevation,
          zIndex: d.zIndex,
          productName: d.productName,
          label: d.label,
          textureUrl: d.textureUrl,
          points: d.points ? d.points.map(p => ({ ...p })) : undefined,
        }
      )
      const snapshot = { ...gardenObjectState.getObjectById(newId)! }
      undoStack.push({
        description: `Paste ${d.type}`,
        undo: () => gardenObjectState.deleteObject(newId),
        redo: () => gardenObjectState.restoreObject(snapshot),
      })
      // Auto-select after render (async for GLB models)
      getViewer()?.selectAfterRender(newId)
    } else if (entry.source === 'zone') {
      const d = entry.data
      const newId = zoneState.addZone(
        d.zoneType,
        d.x + PASTE_OFFSET,
        d.y + PASTE_OFFSET,
        d.width,
        d.height,
        {
          rotation: d.rotation,
          elevation: d.elevation,
          shapeKind: d.shapeKind,
          label: d.label,
          notes: d.notes,
          points: d.points ? d.points.map(p => ({ ...p })) : undefined,
        }
      )
      const snapshot = { ...zoneState.getZoneById(newId)! }
      undoStack.push({
        description: `Paste zone`,
        undo: () => zoneState.deleteZone(newId),
        redo: () => zoneState.restoreZone(snapshot),
      })
      const zone = zoneState.getZoneById(newId)
      if (zone) getViewer()?.selectObject(zone)
    } else if (entry.source === 'pin') {
      const d = entry.data
      const newId = zoneState.addPin(
        d.pinType,
        d.x + PASTE_OFFSET,
        d.y + PASTE_OFFSET,
        {
          label: d.label,
          notes: d.notes,
        }
      )
      const snapshot = { ...zoneState.getPinById(newId)! }
      undoStack.push({
        description: `Paste pin`,
        undo: () => zoneState.deletePin(newId),
        redo: () => zoneState.restorePin(snapshot),
      })
      const pin = zoneState.getPinById(newId)
      if (pin) getViewer()?.selectObject(pin)
    }

    updateClipboardButtons()
    debug.log('SelectTool', 'Pasted from clipboard')
  }

  dispose(): void {
    this.clearMultiSelection()
    this.clearSelection()
  }

  // =========================================================================
  // Single-selection handling (unchanged)
  // =========================================================================

  private onSelectionChange = (result: SelectionResult | null): void => {
    // Ignore single-select callbacks while multi-select is active
    // (GardenScene click handler fires after Shift+click and would override multi-select)
    if (this.multiSelection.size > 0) return

    if (result) {
      const shape = this.createAdapterForResult(result)
      if (shape) {
        this.setSelection(shape)
      } else {
        this.clearSelection()
      }
    } else {
      this.clearSelection()
    }
  }

  private createAdapterForResult(result: SelectionResult): EditableShape | null {
    const obj = result.object
    if (obj.type === 'zone') {
      return new ZoneAdapter(obj as Zone)
    }
    if (obj.type === 'rectangle' || obj.type === 'ellipse' || obj.type === 'polygon' || obj.type === 'path') {
      const stateObj = gardenObjectState.getObjectById(obj.id)
      if (stateObj) return new GroundShapeAdapter(stateObj)
    }
    if (obj.type === 'building') {
      const stateObj = gardenObjectState.getObjectById(obj.id)
      if (stateObj) return new BuildingAdapter(stateObj)
    }
    if (obj.type === 'plant') {
      const stateObj = gardenObjectState.getObjectById(obj.id)
      if (stateObj) return new PlantAdapter(stateObj)
    }
    if (obj.type === 'pin') {
      return new PinAdapter(obj as ContextPin)
    }
    if (obj.type === 'boundary') {
      const viewer = getViewer()
      if (viewer) {
        return new BoundaryAdapter(
          () => viewer.getSchemeOutline(),
          (o) => viewer.setSchemeOutline(o),
          () => viewer.getGarden()?.rotation ?? 0,
          (deg) => viewer.setGardenRotation(deg),
        )
      }
    }
    return null
  }

  private setSelection(shape: EditableShape): void {
    this.selectedShape = shape
    // Skip handles for locked objects (no resize/rotate handles)
    if (!isObjectLocked(shape.sourceType, shape.id)) {
      const accentColor = shape.getAccentColor?.() ?? 0x4ecca3
      this.handleManager.createHandlesFor(shape, accentColor)
    } else {
      this.handleManager.clearHandles()
    }
    updateClipboardButtons()
    debug.log('SelectTool', `Selected shape: ${shape.sourceType} ${shape.id}`)
  }

  private clearSelection(): void {
    this.selectedShape = null
    this.hoveredHandle = null
    this.handleManager.clearHandles()
    this.dragMode = 'none'
    this.activeHandle = null
    this.dragStartWorld = null
    this.dragStartCenter = null
    updateClipboardButtons()
  }

  /** Update highlight outline to follow current selection position */
  private updateHighlight(): void {
    const result = this.selectionManager.getSelectedResult()
    if (result && this.highlightManager) {
      // Refresh object data from mesh userData (may have been updated in-place by renderers)
      const data = result.mesh.userData
      const freshObject = data.plantData || data.buildingData || data.shapeData || data.boundaryData || data.zoneData || data.pinData
      const freshResult = freshObject
        ? { object: freshObject as SelectionResult['object'], mesh: result.mesh }
        : result
      const locked = this.selectedShape
        ? isObjectLocked(this.selectedShape.sourceType, this.selectedShape.id)
        : false
      this.highlightManager.updateSelection(freshResult, locked ? 0xFFA726 : undefined)
    }
  }

  // =========================================================================
  // Single-object deletion
  // =========================================================================

  private deleteSingleSelection(): void {
    if (!this.selectedShape) return

    const { sourceType, id } = this.selectedShape

    if (sourceType === 'zone') {
      const zone = zoneState.getZoneById(id)
      if (zone) {
        const snapshot = { ...zone }
        this.selectionManager.deselect()
        zoneState.deleteZone(id)
        undoStack.push({
          description: `Delete ${id}`,
          undo: () => zoneState.restoreZone(snapshot),
          redo: () => zoneState.deleteZone(id),
        })
        debug.log('SelectTool', `Deleted zone: ${id}`)
      }
    } else if (sourceType === 'building' || sourceType === 'plant' || sourceType === 'groundShape') {
      const obj = gardenObjectState.getObjectById(id)
      if (obj) {
        const snapshot = { ...obj }
        this.selectionManager.deselect()
        gardenObjectState.deleteObject(id)
        undoStack.push({
          description: `Delete ${sourceType} ${id}`,
          undo: () => gardenObjectState.restoreObject(snapshot),
          redo: () => gardenObjectState.deleteObject(id),
        })
        debug.log('SelectTool', `Deleted ${sourceType}: ${id}`)
      }
    } else if (sourceType === 'pin') {
      const pin = zoneState.getPinById(id)
      if (pin) {
        const snapshot = { ...pin }
        this.selectionManager.deselect()
        zoneState.deletePin(id)
        undoStack.push({
          description: `Delete pin ${id}`,
          undo: () => zoneState.restorePin(snapshot),
          redo: () => zoneState.deletePin(id),
        })
        debug.log('SelectTool', `Deleted pin: ${id}`)
      }
    } else if (sourceType === 'boundary') {
      const viewer = getViewer()
      if (viewer) {
        const outline = viewer.getSchemeOutline()
        if (outline) {
          const snapshot = { x: outline.x, y: outline.y, points: outline.points.map(p => ({ ...p })) }
          this.selectionManager.deselect()
          viewer.clearSchemeOutline()
          undoStack.push({
            description: 'Delete boundary',
            undo: () => viewer.setSchemeOutline(snapshot),
            redo: () => viewer.clearSchemeOutline(),
          })
          debug.log('SelectTool', 'Deleted boundary')
        }
      }
    }
  }

  // =========================================================================
  // Lock toggle
  // =========================================================================

  private toggleLockSelection(): void {
    if (this.multiSelection.size > 0) {
      // Multi-select: if any unlocked → lock all; if all locked → unlock all
      const entries = [...this.multiSelection.values()]
      const allLocked = entries.every(e => isObjectLocked(e.shape.sourceType, e.shape.id))
      const newLocked = !allLocked
      const states = entries.map(e => ({
        sourceType: e.shape.sourceType,
        id: e.shape.id,
        wasLocked: isObjectLocked(e.shape.sourceType, e.shape.id),
      }))
      for (const s of states) {
        setObjectLocked(s.sourceType, s.id, newLocked)
      }
      undoStack.push({
        description: `${newLocked ? 'Lock' : 'Unlock'} ${states.length} objects`,
        undo: () => { for (const s of states) setObjectLocked(s.sourceType, s.id, s.wasLocked) },
        redo: () => { for (const s of states) setObjectLocked(s.sourceType, s.id, newLocked) },
      })
    } else if (this.selectedShape) {
      const { sourceType, id } = this.selectedShape
      if (sourceType === 'boundary') return // Boundaries cannot be locked
      const wasLocked = isObjectLocked(sourceType, id)
      setObjectLocked(sourceType, id, !wasLocked)
      undoStack.push({
        description: `${wasLocked ? 'Unlock' : 'Lock'} ${id}`,
        undo: () => setObjectLocked(sourceType, id, wasLocked),
        redo: () => setObjectLocked(sourceType, id, !wasLocked),
      })
      // Rebuild handles and highlight
      this.setSelection(this.selectedShape)
      this.updateHighlight()
    }
  }

  // =========================================================================
  // Vertex operations (single-select only)
  // =========================================================================

  private insertAndDragVertex(midpoint: MidpointHandle, worldPoint: THREE.Vector3): Handle | null {
    if (!this.selectedShape) return null

    const points = this.selectedShape.getPoints()
    if (!points) return null

    const a = points[midpoint.edgeStart]
    const b = points[midpoint.edgeEnd]
    const newPoint = {
      x: Math.round((a.x + b.x) / 2),
      y: Math.round((a.y + b.y) / 2),
    }

    const insertIndex = midpoint.edgeStart + 1
    const newPoints = [...points]
    newPoints.splice(insertIndex, 0, newPoint)

    this.selectedShape.beginEdit()
    this.selectedShape.setPoints(newPoints)
    this.updateBoundingBox(newPoints)

    const accentColor = this.selectedShape.getAccentColor?.() ?? 0x4ecca3
    this.handleManager.createHandlesFor(this.selectedShape, accentColor)

    const newHandle = this.handleManager.getHandles().find(
      h => h.type === 'vertex' && (h as VertexHandle).index === insertIndex
    ) ?? null

    if (newHandle) {
      newHandle.onDragStart(worldPoint)
    }

    debug.log('SelectTool', `Inserted vertex at ${insertIndex}, starting drag`)
    return newHandle
  }

  private deleteVertex(vertexHandle: VertexHandle): void {
    if (!this.selectedShape) return

    const points = this.selectedShape.getPoints()
    if (!points || points.length <= 3) return

    const newPoints = points.filter((_, i) => i !== vertexHandle.index)

    this.selectedShape.beginEdit()
    this.selectedShape.setPoints(newPoints)
    this.updateBoundingBox(newPoints)
    this.selectedShape.commit()

    const accentColor = this.selectedShape.getAccentColor?.() ?? 0x4ecca3
    this.handleManager.createHandlesFor(this.selectedShape, accentColor)
    this.hoveredHandle = null

    debug.log('SelectTool', `Deleted vertex ${vertexHandle.index}, ${newPoints.length} remaining`)
  }

  private updateBoundingBox(points: Array<{ x: number; y: number }>): void {
    if (!this.selectedShape) return
    const xs = points.map(p => p.x)
    const ys = points.map(p => p.y)
    const width = Math.max(...xs) - Math.min(...xs)
    const height = Math.max(...ys) - Math.min(...ys)
    this.selectedShape.resize(Math.max(1, width), Math.max(1, height))
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  private getObjectKey(result: SelectionResult): string {
    const obj = result.object
    if (obj.type === 'zone') return `zone_${obj.id}`
    if (obj.type === 'pin') return `pin_${obj.id}`
    return `${obj.type}_${obj.id}`
  }

  private getNDC(ctx: ToolContext): THREE.Vector2 {
    const rect = this.toolManager['domElement'].getBoundingClientRect()
    return new THREE.Vector2(
      ((ctx.event.clientX - rect.left) / rect.width) * 2 - 1,
      -((ctx.event.clientY - rect.top) / rect.height) * 2 + 1
    )
  }

  private hitTestSelectedMesh(ctx: ToolContext): boolean {
    if (!this.selectedShape) return false

    const scene = ctx.scene
    let mesh: THREE.Object3D | null = null

    if (this.selectedShape.sourceType === 'zone') {
      mesh = scene.getObjectByName(`zone_${this.selectedShape.id}`) ?? null
    } else if (this.selectedShape.sourceType === 'groundShape') {
      mesh = scene.getObjectByName(`groundShape_${this.selectedShape.id}`) ?? null
    } else if (this.selectedShape.sourceType === 'building') {
      const id = this.selectedShape.id
      scene.traverse((child) => {
        if (!mesh && child.userData.buildingId === id) {
          mesh = child
        }
      })
    } else if (this.selectedShape.sourceType === 'plant') {
      const id = this.selectedShape.id
      let fallback: THREE.Object3D | null = null
      scene.traverse((child) => {
        if (child.userData.plantId === id) {
          if (child.visible && !mesh) {
            mesh = child
          } else if (!fallback) {
            fallback = child
          }
        }
      })
      if (!mesh) mesh = fallback
    } else if (this.selectedShape.sourceType === 'pin') {
      mesh = scene.getObjectByName(`pin_${this.selectedShape.id}`) ?? null
    } else if (this.selectedShape.sourceType === 'boundary') {
      mesh = scene.getObjectByName('boundary_outline') ?? null
    }

    if (!mesh) return false

    const ndc = this.getNDC(ctx)
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(ndc, ctx.camera)

    const intersects = raycaster.intersectObject(mesh, true)
    return intersects.length > 0
  }

  /** Check if multi-select is currently active (for external use, e.g. props panel) */
  isMultiSelectActive(): boolean {
    return this.multiSelection.size > 0
  }

  /** Get the count of multi-selected objects */
  getMultiSelectCount(): number {
    return this.multiSelection.size
  }
}
